import { useState } from 'react';
import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { useApolloClient } from '@apollo/client/react';
import { useMaturityFrameworks, useMaturityFramework, useService } from '../../graphql/queries';
import { AssessmentForm } from '../../components/maturity';
import { createMaturityAssessment, ApiError } from '../../lib/apiClient';
import { LIST_MATURITY_ASSESSMENTS } from '../../graphql/queries';
import { MaturityFrameworkStatus, MaturitySubjectEntityType } from '../../graphql/types';
import type { CreateMaturityAssessmentFormData } from '../../components/forms/validation';
import { DetailSkeleton, EmptyState } from '../../components/shared';
import '@tylertech/forge/banner';
import '@tylertech/forge/select';
import '@tylertech/forge/button';
import './$serviceId.assessments.new.css';

interface NewAssessmentSearch {
  /** Assess against a specific framework (e.g. a deep link carrying
   * `?frameworkId=`) instead of falling back to whichever framework happens
   * to be Active. */
  frameworkId?: string;
}

export const Route = createFileRoute('/services/$serviceId/assessments/new')({
  component: NewAssessmentPage,
  validateSearch: (search: Record<string, unknown>): NewAssessmentSearch => ({
    frameworkId: typeof search.frameworkId === 'string' ? search.frameworkId : undefined,
  }),
});

export { NewAssessmentPage };

function NewAssessmentPage(): JSX.Element {
  const { serviceId } = Route.useParams();
  const { frameworkId: requestedFrameworkId } = Route.useSearch();
  const navigate = useNavigate();
  const apolloClient = useApolloClient();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Explicit user choice from the framework-selection step below (#1603) —
  // stays undefined until the user picks one, so nothing is auto-selected
  // when 2+ frameworks exist and none was requested via search param.
  const [selectedFrameworkId, setSelectedFrameworkId] = useState<string | undefined>(undefined);
  const [pickerDraftId, setPickerDraftId] = useState('');

  const hasRequestedFramework = requestedFrameworkId !== undefined;

  // A specific framework was requested (via `?frameworkId=`) — fetch it
  // directly instead of picking whichever framework is Active.
  const {
    data: requestedFrameworkData,
    loading: requestedFrameworkLoading,
    error: requestedFrameworkError,
  } = useMaturityFramework(requestedFrameworkId ?? '');

  const {
    data,
    loading: frameworksLoading,
    error: frameworksFetchError,
  } = useMaturityFrameworks(20, hasRequestedFramework);

  // Resolve the subject service's owning team so the create payload can carry
  // a real ownerId (#1567). Assessments are always team-owned server-side —
  // MaturityAssessmentRepository hardcodes ownerType: 'Team' regardless of
  // what's submitted — so an unowned service falls back to the service itself
  // as the notification-target sentinel rather than blocking creation. The
  // backend never writes this sentinel into GSI3's TEAM# namespace — see
  // maturityAssessmentGsi3Key (#1598) — it only accepts a real Team id there.
  const { data: serviceData } = useService(serviceId);
  const ownerId = serviceData?.service?.teamId ?? serviceId;

  const loading = hasRequestedFramework ? requestedFrameworkLoading : frameworksLoading;
  const fetchError = hasRequestedFramework ? requestedFrameworkError : frameworksFetchError;

  const frameworks = data?.maturityFrameworks?.edges?.map((e) => e.node) ?? [];

  // Per maturity-model-spec.md §12.2 ("new assessments use the latest active
  // framework"), the picker only ever offers Active frameworks — Draft/
  // Deprecated are not valid assessment targets. Only when *no* framework is
  // Active do we fall back to the old last-resort behavior (frameworks[0],
  // picked arbitrarily, no picker) rather than blocking assessment creation
  // outright.
  const activeStatusFrameworks = frameworks.filter(
    (f) => f.status === MaturityFrameworkStatus.Active
  );
  const pickableFrameworks =
    activeStatusFrameworks.length > 0 ? activeStatusFrameworks : frameworks;

  // Single-framework case is unambiguous — same auto-select as before (#1603
  // keeps this path unchanged). Multi-framework case requires an explicit
  // user choice; nothing is picked on the user's behalf.
  const activeFramework = hasRequestedFramework
    ? (requestedFrameworkData?.maturityFramework ?? undefined)
    : pickableFrameworks.length === 1
      ? pickableFrameworks[0]
      : pickableFrameworks.find((f) => f.entityId === selectedFrameworkId);

  const needsFrameworkPicker =
    !hasRequestedFramework && pickableFrameworks.length > 1 && activeFramework === undefined;

  const noFrameworkDescription =
    fetchError?.message ??
    'An active maturity framework is required before creating an assessment. Ask an administrator to publish one.';

  const BannerElement = 'forge-banner' as React.ElementType;
  const ForgeSelect = 'forge-select' as React.ElementType;
  const ForgeOption = 'forge-option' as React.ElementType;
  const ForgeButton = 'forge-button' as React.ElementType;

  if (loading) {
    return (
      <div className="new-assessment-page">
        <DetailSkeleton cards={1} />
      </div>
    );
  }

  if (fetchError || (!hasRequestedFramework && frameworks.length === 0)) {
    return (
      <div className="new-assessment-page">
        <EmptyState
          icon="assignment_late"
          title="No active framework"
          description={noFrameworkDescription}
          actionLabel="Back"
          onAction={() =>
            void navigate({ to: '/services/$serviceId/assessments', params: { serviceId } })
          }
        />
      </div>
    );
  }

  if (needsFrameworkPicker) {
    return (
      <div className="new-assessment-page">
        <div className="page-header">
          <Link to="/services/$serviceId/assessments" params={{ serviceId }} className="back-link">
            ← Assessments
          </Link>
          <h1 className="forge-typography--headline5">New Assessment</h1>
          <p className="forge-typography--body2">
            This service has more than one maturity framework. Choose one to continue.
          </p>
        </div>

        <ForgeSelect
          name="frameworkId"
          label="Framework"
          value={pickerDraftId}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setPickerDraftId(e.target.value)}
        >
          <ForgeOption value="">Select a framework</ForgeOption>
          {pickableFrameworks.map((f) => (
            <ForgeOption key={f.entityId} value={f.entityId}>
              {f.name} v{f.version}
            </ForgeOption>
          ))}
        </ForgeSelect>

        <ForgeButton
          type="button"
          variant="raised"
          disabled={pickerDraftId === ''}
          onClick={() => setSelectedFrameworkId(pickerDraftId)}
        >
          Continue
        </ForgeButton>
      </div>
    );
  }

  if (!activeFramework) {
    return (
      <div className="new-assessment-page">
        <EmptyState
          icon="assignment_late"
          title="No active framework"
          description={noFrameworkDescription}
          actionLabel="Back"
          onAction={() =>
            void navigate({ to: '/services/$serviceId/assessments', params: { serviceId } })
          }
        />
      </div>
    );
  }

  const handleSubmit = async (formData: CreateMaturityAssessmentFormData): Promise<void> => {
    setIsSubmitting(true);
    setError(null);
    try {
      const created = await createMaturityAssessment({
        name: `${formData.assessmentPeriod} Assessment`,
        frameworkId: activeFramework.entityId,
        subjectEntityType: MaturitySubjectEntityType.Service,
        subjectEntityId: serviceId,
        assessmentPeriod: formData.assessmentPeriod,
        dueDate: formData.dueDate,
        description: formData.description,
        ownerType: 'Team',
        ownerId,
        responses: formData.responses?.map((r) => ({
          capabilityId: r.capabilityId,
          level: r.level,
          comment: r.comment,
          respondedAt: new Date().toISOString(),
        })),
      });

      await apolloClient.refetchQueries({ include: [LIST_MATURITY_ASSESSMENTS] });

      void navigate({
        to: '/services/$serviceId/assessments/$assessmentId',
        params: { serviceId, assessmentId: created.entityId },
      });
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to create assessment';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = (): void => {
    void navigate({ to: '/services/$serviceId/assessments', params: { serviceId } });
  };

  return (
    <div className="new-assessment-page">
      <div className="page-header">
        <Link to="/services/$serviceId/assessments" params={{ serviceId }} className="back-link">
          ← Assessments
        </Link>
        <h1 className="forge-typography--headline5">New Assessment</h1>
        <p className="forge-typography--body2">
          Framework: {activeFramework.name} v{activeFramework.version}
        </p>
      </div>

      {error && (
        <BannerElement theme="danger">
          <span>{error}</span>
        </BannerElement>
      )}

      <AssessmentForm
        framework={activeFramework}
        mode="create"
        serviceId={serviceId}
        defaultValues={{
          frameworkId: activeFramework.entityId,
          subjectEntityType: MaturitySubjectEntityType.Service,
          subjectEntityId: serviceId,
        }}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isSubmitting={isSubmitting}
      />
    </div>
  );
}
